import { useContext, useEffect, useState } from 'react';
import { Configure, InstantSearch, useHits } from 'react-instantsearch';

import { useAlgoliaUserToken } from '@/hooks/useAlgoliaUserToken';
import { getIndexName } from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { StoreHit } from '@/integrations/algolia/types';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { LocaleContext } from '@/providers/LocaleProvider';
import { listingActions } from '@/states/listingState';

import type { ExhibitionItem } from '../../ExhibitionList';
import type { PastExhibitionItemType } from '../PastExhibitionItem/PastExhibitionItem';
import { PastExhibitionItem } from '../PastExhibitionItem/PastExhibitionItem';

import * as S from '../../ExhibitionList.styles';

export const PastExhibitionItemsContent = ({
  pastExhibitions,
}: {
  pastExhibitions: ExhibitionItem[];
}) => {
  const { hits } = useHits<StoreHit>();
  const results = pastExhibitions?.map((item) => {
    const matchingHit = hits.find((hit) => hit.store_id === item.boutiqueId);
    return {
      ...item,
      ...matchingHit,
    };
  });
  const hitsLength = hits?.length;
  return (
    <S.PastExhibitionItemsContentWrapper>
      {results?.map((boutique, index: number) => {
        const imagePosition = index % 2 === 0 ? 'right' : 'left';
        let sizeType = '';

        switch (hitsLength) {
          case 1:
            sizeType = 'firstAndLast';
            break;
          case 2:
            sizeType = index === 0 ? 'first' : 'last';
            break;
          default:
            if (index === 0) {
              sizeType = 'first';
            } else if (index === hitsLength - 1) {
              sizeType = 'last';
            } else {
              sizeType = 'middle';
            }
            break;
        }
        return (
          <PastExhibitionItem
            key={boutique.boutiqueId}
            boutique={boutique as PastExhibitionItemType['boutique']}
            imagePosition={imagePosition}
            sizeType={sizeType as PastExhibitionItemType['sizeType']}
          />
        );
      })}
    </S.PastExhibitionItemsContentWrapper>
  );
};

export const PastExhibitionItemsContainer = ({
  pastExhibitions,
}: {
  pastExhibitions: ExhibitionItem[];
}) => {
  const draftMode = useContext(DraftModeContext);
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const locale = useContext(LocaleContext);
  const { userToken } = useAlgoliaUserToken();
  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
  });
  const indexName = getIndexName('STORE', locale);

  useEffect(() => {
    listingActions.updateListingType('STORE');
  }, []);
  useEffect(() => {
    if (pastExhibitions?.length > 0) {
      const boutiqueIds = pastExhibitions?.map((item) => item?.boutiqueId);
      const transformedIds = boutiqueIds.map((id) => `store_id:${id}`);
      setStoreIds(transformedIds);
    }
  }, [pastExhibitions]);

  return (
    <InstantSearch
      indexName={indexName}
      insights={{ insightsInitParams: { userToken } as any }}
      searchClient={algoliaSearchClient}
    >
      <Configure facetFilters={[[...storeIds], 'web_visible:true']} />
      <PastExhibitionItemsContent pastExhibitions={pastExhibitions} />
    </InstantSearch>
  );
};
